<ng-container *transloco="let t">
  <div class="col flex flex-col gap-10">
    <div class="card w-full flex flex-col gap-2" style="margin-bottom: 0">
      <h1 class="font-semibold text-xl">{{ t("app.organization-selection.header") }}</h1>
      <p>
        {{ t("app.organization-selection.subtitle") }}
      </p>
    </div>

    @if (pendingInvitations$ | async; as pendingInvitations) {
      <p-card>
        <ng-template #title>
          <div class="flex justify-between">
            <h2>{{ t("app.organization-selection.invitations.header") }}</h2>
            <!-- Refresh button -->
            <p-button
              icon="pi pi-refresh"
              variant="outlined"
              (click)="refreshInvites()"
              [pTooltip]="t('app.organization-selection.invitations.refreshTooltip')"
              tooltipPosition="top"
            />
          </div>
        </ng-template>
        <p-table [value]="pendingInvitations" [showLoader]="false">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t("app.organization-selection.columnHeader.organizationName") }}</th>
              <th class="max-sm:hidden">{{ t("app.organization-selection.columnHeader.validUntil") }}</th>
              <th>{{ t("app.organization-selection.columnHeader.role") }}</th>
              <th>
                <div class="flex justify-end">{{ t("app.organization-selection.columnHeader.acceptReject") }}</div>
              </th>
            </tr>
          </ng-template>

          <!-- Table content -->
          <ng-template pTemplate="body" let-item let-i="rowIndex">
            <tr>
              <td>{{ item.organizationName }}</td>
              <td class="max-sm:hidden">{{ item.expiresAt | ranDate: "dateLong" }}</td>
              <td>
                @if (item.userAccessRole) {
                  <p-tag
                    [severity]="getAccessRoleColorSeverity(item.userAccessRole.color)"
                    [value]="item.userAccessRole.name"
                  />
                }
              </td>
              <td>
                <div class="flex justify-end items-stretch gap-2">
                  <!-- Accept invite button -->
                  <p-button
                    icon="pi pi-check"
                    (onClick)="acceptInvitation(item)"
                    [loading]="item.loading"
                    [pTooltip]="t('app.organization-selection.invitations.acceptTooltip')"
                    tooltipPosition="top"
                  />
                  <!-- Reject invite button -->
                  <p-button
                    icon="pi pi-times"
                    severity="danger"
                    variant="outlined"
                    (onClick)="rejectInvitation(item)"
                    [loading]="item.loading"
                    [pTooltip]="t('app.organization-selection.invitations.rejectTooltip')"
                    tooltipPosition="top"
                  />
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    }

    <p-card class="searchCard">
      <p-iconfield>
        <input
          pInputText
          [ngModel]="orgSearchInputValue$ | async"
          (ngModelChange)="orgSearchInputValue$.next($event)"
          placeholder="{{ t('app.organization-selection.searchPlaceholder') }}"
          class="w-full h-10 p-2"
        />
        <p-inputicon styleClass="pi pi-search" />
      </p-iconfield>
    </p-card>

    <p-card>
      <p-table
        [value]="(filteredOrgList$ | async)!"
        [loading]="loading"
        [showLoader]="false"
        [rowHover]="loading ? false : true"
      >
        <ng-template pTemplate="header">
          <tr>
            <th class="w-40/100">{{ t("app.organization-selection.columnHeader.name") }}</th>
            <th class="w-30/100 max-sm:hidden">{{ t("app.organization-selection.columnHeader.businessId") }}</th>
            <th class="w-25/100">{{ t("app.organization-selection.columnHeader.role") }}</th>
            <th class="w-5/100"></th>
          </tr>
        </ng-template>

        <!-- Table content -->
        <ng-template pTemplate="body" let-item let-i="rowIndex">
          <tr (click)="selectOrganization(item)" [class]="{ 'cursor-pointer': !loading }">
            <td>
              <div class="flex items-center gap-2">
                <span>{{ item.name }}</span>
                @if ((notificationCountByOrg$ | async)?.get(item.id); as count) {
                  <div class="relative inline-block">
                    <i class="pi pi-bell text-gray-600 dark:text-white"></i>
                    <span
                      class="absolute -top-1.5 left-[7px] inline-flex items-center justify-center min-w-[16px] min-h-[16px] px-1.5 py-0.5 text-[9px] font-bold leading-none text-white bg-red-500 rounded-full"
                    >
                      {{ count > 99 ? "99+" : count }}
                    </span>
                  </div>
                }
              </div>
            </td>
            <td class="max-sm:hidden">{{ item.businessId }}</td>
            <td>
              @if (item.internalUserResponsibility) {
                <p-tag
                  [severity]="getResponsibilitySeverity(item.internalUserResponsibility)"
                  [value]="t(getResponsibilityTranslationKey(item.internalUserResponsibility))"
                />
              } @else if (item.accessRole) {
                <p-tag [severity]="getAccessRoleColorSeverity(item.accessRole.color)" [value]="item.accessRole.name" />
              }
            </td>
            <td>
              <i class="pi pi-angle-right"></i>
            </td>
          </tr>
        </ng-template>

        <!-- Loading skeleton template -->
        <ng-template pTemplate="loadingbody">
          @for (_ of [1, 2, 3]; track $index) {
            <tr>
              @for (_ of [1, 2, 3, 4]; track $index) {
                <td><p-skeleton height="2rem"></p-skeleton></td>
              }
            </tr>
          }
        </ng-template>

        <!-- Empty table message -->
        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="4" class="py-6">
              <div class="flex flex-row items-center h-16 justify-center gap-5">
                <i class="pi pi-info-circle text-lg text-blue-500"></i>
                <p class="text-lg">
                  @if (orgSearchInputValue$ | async) {
                    {{ t("app.organization-selection.noOrgsFound") }}
                  } @else {
                    {{
                      isUserInternal
                        ? t("app.organization-selection.noOrgsFoundInternal")
                        : t("app.organization-selection.noOrgsFoundExternal")
                    }}
                  }
                </p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
  </div>
</ng-container>
